import { useTranslations } from 'next-intl';
import { generateFAQPageSchema } from '@/lib/seo/schema';
import JsonLd from '@/components/core/JsonLd';
import type { PlaylistContentData } from '@/lib/data/loaders/mongodb/playlists';
import type { PlaylistFaq } from '@/lib/data/types';

interface PlaylistFAQsProps {
  playlistContent: PlaylistContentData;
}

const PlaylistFAQs = ({ playlistContent }: PlaylistFAQsProps) => {
  const t = useTranslations('common');

  // Get FAQs from the playlist content and filter out invalid ones
  const faqs = (playlistContent.faqs || []).filter(faq => faq.question && faq.answer);

  if (faqs.length === 0) {
    return null;
  }

  // Helper function to strip HTML tags for schema (schema.org prefers plain text)
  const stripHtml = (html: string | undefined): string => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  // Generate FAQ schema for SEO
  const faqSchema = generateFAQPageSchema(
    faqs.map(faq => ({
      question: stripHtml(faq.question),
      answer: stripHtml(faq.answer)
    }))
  );

  return (
    <>
      {/* JSON-LD Schema for FAQs */}
      <JsonLd data={faqSchema} id={`playlist-faq-schema-${playlistContent.slug}`} />
      
      <div className="mt-5 pt-md-3">
        {/* Section Heading */}
        <h2 className="h3">
          <span className="text-gradient-primary">
            {t('playlists.pages.detail.components.faqs.title')}
          </span>
        </h2>

        <div className="accordion accordion-flush" id="playlistFaqAccordion">
          {faqs.map((faq, index) => (
            <div className="accordion-item border-0 mb-3" key={index}>
              <h3 className="accordion-header" id={`heading${index}`}>
                <button
                  className={`accordion-button ${index !== 0 ? 'collapsed' : ''} rounded-4`}
                  style={{ 
                    backgroundColor: '#23262F',
                    border: '1px solid #353945'
                  }}
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#collapse${index}`}
                  aria-expanded={index === 0 ? 'true' : 'false'}
                  aria-controls={`collapse${index}`}
                >
                  <span dangerouslySetInnerHTML={{ __html: faq.question }} />
                </button>
              </h3>
              <div
                id={`collapse${index}`}
                className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                aria-labelledby={`heading${index}`}
                data-bs-parent="#playlistFaqAccordion"
              >
                <div 
                  className="accordion-body rounded-bottom-4"
                  style={{ 
                    backgroundColor: '#141416',
                    border: '1px solid #353945',
                    borderTop: 'none'
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default PlaylistFAQs; 